import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Briefcase, Calendar, CheckCircle2, Clock3, CreditCard, FileCheck, FileUp, Home, UserCheck } from 'lucide-react';
import PortalPageHeader from '../../components/portal/PortalPageHeader';
import { FIREBASE_CONFIG, LAW_FIRM_INFO, CASE_TEAM_ROLES } from '../../config/Config';
import { usePortalContext } from '../../context/PortalContext';
import { auth } from '../../shared/api/firebase/config';
import {
  addClientNote,
  watchClientCases,
  watchClientWorkspaces,
  watchPendingSignatures,
  watchPendingInvoices,
  watchDocumentRequests,
  addToTriage,
  updateDocumentRequestStatus,
} from '../../services/ClientDashboardRepository.js';
import { logClientPortalEventOnce } from '../../utils/portalEventLog';
import { toTitleCasePt } from '../../utils/formatters';
import ForensicTooltip from '../../components/portal/ForensicTooltip';
import ClientTasksCard from '../../components/portal/ClientTasksCard';

// Resolve o próximo evento agendado — prioriza dados do workspace (admin), fallback crmClient
const resolveNextEvent = (source) => {
  const raw = source?.nextEventDate;
  if (!raw) return null;
  let d;
  try {
    d = typeof raw?.toDate === 'function' ? raw.toDate() : new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
  } catch { return null; }
  if (d.getTime() < Date.now()) return null;
  const label    = String(source?.nextEventLabel    || '').trim() || 'Próximo evento';
  const location = String(source?.nextEventLocation || '').trim();
  const formatted = d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return { label, formatted, time, location, date: d };
};

const getBackendBaseUrl = () =>
 {
  const envUrl = String(import.meta?.env?.VITE_BACKEND_URL || '').trim();
  if (envUrl) return envUrl.replace(/\/+$/, '');
  return 'https://rda-backend-thuq.onrender.com';
};

const STATUS_META = {
  pending: {
    label: 'Pendente',
    badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  reviewing: {
    label: 'Em Análise',
    badgeClass: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  approved: {
    label: 'Aprovado',
    badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
};

const resolveClientName = (crmClient, email) => {
  const direct = String(crmClient?.fullName || '').trim();
  if (direct) return direct;

  const physical = crmClient?.qualification?.physical || {};
  const physicalName = String(physical?.socialName || physical?.name || '').trim();
  if (physicalName) return physicalName;

  const contact = crmClient?.contact || {};
  const contactName = String(contact?.name || '').trim();
  if (contactName) return contactName;

  const safeEmail = String(email || '').trim();
  if (safeEmail) return safeEmail.split('@')[0];

  return 'Cliente';
};

const normalizeRequestStatus = (status) => {
  const safe = String(status || '').trim().toLowerCase();
  if (safe === 'approved' || safe === 'done' || safe === 'completed') return 'approved';
  if (safe === 'reviewing' || safe === 'pending_review' || safe === 'in_review' || safe === 'uploaded') return 'reviewing';
  return 'pending';
};

const sanitizeFileName = (value) => {
  return String(value || 'arquivo')
    .replace(/[/\\]/g, '_')
    .replace(/\s+/g, ' ')
    .trim() || 'arquivo';
};

// GED BIFURCADO: uploadPortalFileToStorage removido.
// O upload agora vai para o Google Drive via useDriveUpload hook (veja handleAttach/handleAttachExtra).

// ── MicroFeedback — feedback contextual de 1 toque (emoji 3 opções) ─────────────
function MicroFeedback({ context, clientId, appId }) {
  const STORAGE_KEY = `rda_mfb_${context}`;
  const [done, setDone] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  if (done) return null;

  const OPTIONS = [
    { id: 'easy', emoji: '😊', label: 'Fácil' },
    { id: 'ok',   emoji: '😐', label: 'Regular' },
    { id: 'hard', emoji: '😟', label: 'Difícil' },
  ];

  const handlePick = async (opt) => {
    setDone(true);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
    if (!clientId || !appId) return;
    try {
      await addClientNote(clientId, {
        type: 'PORTAL_MICROFEEDBACK',
        category: 'FEEDBACK',
          payload: { context, rating: opt.id, label: opt.label },
        }
      );
    } catch { void 0; }
  };

  return (
    <div className="mt-3 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
      <div className="text-[11px] font-bold text-gray-500 mb-2">Foi fácil enviar este documento?</div>
      <div className="flex gap-2">
        {OPTIONS.map(opt => (
          <button
            key={opt.id}
            type="button"
            onClick={() => handlePick(opt)}
            className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl border border-gray-200 bg-white hover:border-[#C5A059] hover:bg-amber-50 transition-all"
          >
            <span className="text-2xl leading-none">{opt.emoji}</span>
            <span className="text-[10px] font-bold text-gray-500">{opt.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const DspeSlotCard = ({ item, onNavigate }) => (
  <div className="flex items-center gap-2.5 px-3 py-2 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors">
    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
    <FileUp className="w-3.5 h-3.5 text-amber-500 shrink-0" />
    <div className="flex-1 min-w-0">
      <span className="text-[12px] font-bold text-[#20243B] truncate block">{item.title}</span>
    </div>
    <span className="text-[9px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 shrink-0">Pendente</span>
    <button
      type="button"
      onClick={onNavigate}
      className="shrink-0 text-[10px] font-bold text-white bg-[#20243B] hover:bg-[#C5A059] hover:text-[#20243B] rounded-lg px-2.5 py-1.5 transition-colors"
    >
      Enviar →
    </button>
  </div>
);

const ChecklistCard = ({ item, uploading, uploadError, onAttach, clientId, appId }) => {
  const normalizedStatus = normalizeRequestStatus(item.status);
  const meta = STATUS_META[normalizedStatus] || STATUS_META.pending;
  const inputId = `client-request-upload-${item.id}`;

  const dotColor = normalizedStatus === 'approved' ? 'bg-emerald-500' : normalizedStatus === 'reviewing' ? 'bg-blue-500' : 'bg-amber-500';

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
      <div className="flex items-center gap-2.5">
        <span className={`w-1.5 h-1.5 rounded-full ${dotColor} shrink-0`} />
        {normalizedStatus === 'approved' ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
        ) : normalizedStatus === 'reviewing' ? (
          <Clock3 className="w-3.5 h-3.5 text-blue-500 shrink-0" />
        ) : (
          <FileUp className="w-3.5 h-3.5 text-amber-500 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <span className="text-[12px] font-bold text-[#20243B] truncate block flex items-center gap-1">{item.title}<ForensicTooltip slotName={item.title} /></span>
        </div>
        <span className={`text-[9px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 border shrink-0 ${meta.badgeClass}`}>{meta.label}</span>

        {normalizedStatus === 'pending' && (
          <label htmlFor={inputId} className={`shrink-0 text-[10px] font-bold rounded-lg px-2.5 py-1.5 transition-colors cursor-pointer ${uploading ? 'bg-slate-400 text-white cursor-wait pointer-events-none' : 'bg-[#20243B] text-white hover:bg-[#C5A059] hover:text-[#20243B]'}`}>
            {uploading ? '...' : 'Enviar'}
            <input
              id={inputId}
              type="file"
              className="hidden"
              capture="environment"
              accept="image/*,application/pdf"
              onChange={(e) => onAttach(item, e)}
              disabled={uploading}
            />
          </label>
        )}
      </div>

      {normalizedStatus === 'pending' && uploadError ? (
        <div className="mt-1.5 rounded-lg border border-red-100 bg-red-50 px-2.5 py-1.5 text-[10px] font-bold text-red-800 whitespace-pre-wrap">
          {uploadError}
        </div>
      ) : null}

      {normalizedStatus === 'reviewing' ? (
        <div className="mt-1.5 rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-1.5 text-[10px] font-bold text-blue-800">
          Envio recebido — em análise pelo escritório.
        </div>
      ) : null}

      {normalizedStatus === 'approved' ? (
        <div className="mt-1.5 rounded-lg border border-emerald-100 bg-emerald-50 px-2.5 py-1.5 text-[10px] font-bold text-emerald-800">
          Documento aprovado.
        </div>
      ) : null}
    </div>
  );
};

// ── Art.327 Fase 4: Dashboard do Advogado Externo ────────────────────────────
const ROLE_LABELS_PORTAL = { LEAD: 'Principal', ASSOCIATE: 'Associado', COCOUNSEL: 'Correspondente', PARALEGAL: 'Paralegal' };
const STAGE_LABELS = {
  inicial: 'Fase Inicial', instrucao: 'Instrução', recurso: 'Recurso',
  execucao: 'Execução', encerrado: 'Encerrado', arquivado: 'Arquivado',
};

function LawyerDashboard() {
  const { crmClient, email } = usePortalContext();
  const navigate = useNavigate();
  const clientId = useMemo(() => String(crmClient?.id || '').trim(), [crmClient?.id]);
  const appId = useMemo(() => String(FIREBASE_CONFIG?.projectId || '').trim(), []);
  const clientName = useMemo(() => resolveClientName(crmClient, email), [crmClient, email]);

  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId || !appId) { setCases([]); setLoading(false); return; }
    setLoading(true);
    const unsub = watchClientCases(
      clientId,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        rows.sort((a, b) => {
          const ta = a?.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : 0;
          const tb = b?.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : 0;
          return tb - ta;
        });
        setCases(rows);
        setLoading(false);
      },
      () => { setCases([]); setLoading(false); }
    );
    return () => unsub();
  }, [clientId, appId]);

  useEffect(() => {
    if (!clientId) return;
    (async () => {
      try {
        await logClientPortalEventOnce({
          clientId, type: 'PARTNER_PORTAL_SESSION_STARTED',
          actorName: clientName,
          payload: { text: 'Advogado parceiro acessou o portal.' },
          storage: 'session',
        });
      } catch { void 0; }
    })();
  }, [clientId, clientName]);

  const myRole = (ws) => {
    const team = Array.isArray(ws?.caseTeam) ? ws.caseTeam : [];
    const me = team.find((m) => String(m?.clientId || '') === clientId);
    return me?.role || null;
  };

  return (
    <div className="space-y-4">
      <PortalPageHeader
        icon={<Briefcase className="w-5 h-5 text-[#C5A059]" />}
        title={`Olá, ${toTitleCasePt(clientName)}`}
        subtitle="Seus casos vinculados como advogado parceiro."
      />

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center">
          <div className="flex items-center justify-center gap-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-[#C5A059]" />
            <span className="text-sm text-gray-500">Carregando seus casos...</span>
          </div>
        </div>
      ) : cases.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center">
          <Briefcase className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <div className="text-sm font-bold text-[#20243B]">Nenhum caso vinculado</div>
          <div className="text-[11px] text-gray-500 mt-1">Quando o escritório vincular você a um caso, ele aparecerá aqui.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {cases.map((ws) => {
            const role = myRole(ws);
            const stage = ws?.caseStage || ws?.metadata?.caseStage || null;
            const npu = ws?.ancestry?.primary_process_id || null;
            const wsClientName = String(ws?.clientName || '').trim();
            const title = String(ws?.title || ws?.name || '').trim() || wsClientName || 'Caderno sem título';
            return (
              <button
                key={ws.id}
                type="button"
                onClick={() => navigate('/portal/caso')}
                className="w-full text-left rounded-lg border border-gray-200 bg-white hover:border-[#C5A059]/60 hover:shadow-md transition-all overflow-hidden group"
              >
                <div className="flex items-stretch">
                  <div className="w-1.5 shrink-0 bg-[#20243B] group-hover:bg-[#C5A059] transition-colors" />
                  <div className="flex-1 px-3 py-2.5 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      {role && (
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0 ${role === 'LEAD' ? 'bg-[#C5A059] text-white' : 'bg-gray-200 text-gray-700'}`}>
                          {ROLE_LABELS_PORTAL[role] || role}
                        </span>
                      )}
                      {stage && (
                        <span className="text-[9px] font-medium text-gray-500 bg-gray-100 rounded px-1.5 py-0.5 shrink-0">
                          {STAGE_LABELS[stage] || stage}
                        </span>
                      )}
                    </div>
                    <div className="text-[13px] font-bold text-[#20243B] truncate leading-tight">{title}</div>
                    {npu && <div className="text-[10px] text-gray-500 mt-0.5 font-mono">{npu}</div>}
                    {wsClientName && wsClientName !== title && (
                      <div className="text-[10px] text-gray-400 mt-0.5 truncate">Cliente: {toTitleCasePt(wsClientName)}</div>
                    )}
                  </div>
                  <div className="flex items-center pr-3 shrink-0">
                    <i className="fas fa-chevron-right text-[10px] text-gray-400 group-hover:text-[#C5A059] transition-colors" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ClientDashboard() {
  const { crmClient, email, isPartner } = usePortalContext();

  // Art.327: advogado parceiro vê dashboard dedicado (gate antes de hooks do client)
  if (isPartner) return <LawyerDashboard />;

  return <ClientDashboardInner crmClient={crmClient} email={email} />;
}

function ClientDashboardInner({ crmClient, email }) {
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestsError, setRequestsError] = useState('');
  const [uploadingById, setUploadingById] = useState({});
  const [uploadErrorById, setUploadErrorById] = useState({});
  const [presenceConfirmed, setPresenceConfirmed] = useState(() => {
    try { return Boolean(window.localStorage.getItem('rda_presence_' + (crmClient?.id || ''))) ; } catch { return false; }
  });
  const [presenceLoading, setPresenceLoading]   = useState(false);
  const [lastUploadMessage, setLastUploadMessage] = useState('');
  const [pendingSignatureCount, setPendingSignatureCount] = useState(0);
  const [pendingInvoiceCount, setPendingInvoiceCount] = useState(0);
  const [portalWs, setPortalWs] = useState(null);
  const [dspePendingSlots, setDspePendingSlots] = useState([]);
  const [myWorkspaces, setMyWorkspaces] = useState([]);

  const clientName = useMemo(() => resolveClientName(crmClient, email), [crmClient, email]);
  const clientId = useMemo(() => String(crmClient?.id || '').trim(), [crmClient?.id]);
  const pendingCount = useMemo(
    () => requests.filter((item) => normalizeRequestStatus(item.status) === 'pending').length + dspePendingSlots.length,
    [requests, dspePendingSlots]
  );
  const appId = useMemo(() => String(FIREBASE_CONFIG?.projectId || '').trim(), []);

  const navigate = useNavigate();

  const allClear = pendingCount === 0 && pendingSignatureCount === 0 && pendingInvoiceCount === 0;

  const handleConfirmPresence = async (ev) => {
    if (presenceConfirmed || presenceLoading || !clientId) return;
    setPresenceLoading(true);
    const storageKey = `rda_presence_${clientId}_${ev.date.toISOString().slice(0, 10)}`;
    try {
      const safeAppId = String(appId || '').trim();
      if (safeAppId) {
        await addClientNote(clientId, {
          type:      'PORTAL_PRESENCE_CONFIRMED',
          actorName: clientName,
            payload:   { text: `Cliente confirmou presença: ${ev.label} em ${ev.formatted}${ev.location ? ` — ${ev.location}` : ''}` },
          }
        );
      }
      try { window.localStorage.setItem(storageKey, '1'); } catch {}
      setPresenceConfirmed(true);
    } catch { /* silent */ }
    setPresenceLoading(false);
  };

  // Matter-Centric Fase 3: buscar workspaces diretos + co-parte (partiesRefIds)
  useEffect(() => {
    if (!clientId || !appId) { setDspePendingSlots([]); setMyWorkspaces([]); setPortalWs(null); return; }
    const unsub = watchClientWorkspaces(
      clientId,
      (direct) => {
        const merged = new Map();
        direct.forEach((w) => merged.set(w.id, w));
        const all = Array.from(merged.values());
        all.sort((a, b) => {
          const ta = a?.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : 0;
          const tb = b?.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : 0;
          return tb - ta;
        });
        setMyWorkspaces(all);
        setPortalWs(all.find(r => r.nextEventDate) || all[0] || null);
        // DSPE pending slots
        const pending = [];
        all.forEach((data) => {
          const orgState = data.organizerState || {};
          const slotOrder = Array.isArray(orgState.slotOrder) ? orgState.slotOrder : [];
          const slotNames = orgState.slotNames || {};
          const assignments = orgState.assignments || {};
          const clientReq = orgState.clientRequested || {};
          slotOrder.forEach((slotId) => {
            if (clientReq[slotId] === false) return;
            if (String(slotId).startsWith('procuracao-')) return;
            if (String(slotId).startsWith('slot-client-extra-')) return;
            const assignment = assignments[slotId];
            if (assignment?.id) return;
            const name = String(slotNames[slotId] || '').trim();
            if (!name) return;
            pending.push({ id: slotId, wsId: data.id, title: name, source: 'dspe' });
          });
        });
        setDspePendingSlots(pending);
      },
      () => { setMyWorkspaces([]); setDspePendingSlots([]); }
    );
    return () => { try { unsub && unsub(); } catch {} };
  }, [clientId, appId]);

  useEffect(() => {
    if (!clientId) { setPendingSignatureCount(0); return; }
    const unsub = watchPendingSignatures(
      clientId,
      (docs) => {
        const pending = docs.filter((row) => {
          if (row?.clientVisible === false) return false;
          if (!row?.requiresConsent) return false;
          const cs = String(row?.consentStatus || '').trim().toLowerCase();
          return !['accepted', 'consent_accepted', 'declined', 'consent_declined'].includes(cs);
        });
        setPendingSignatureCount(pending.length);
      },
      () => setPendingSignatureCount(0)
    );
    return () => { try { unsub && unsub(); } catch {} };
  }, [clientId, appId]);

  useEffect(() => {
    if (!clientId) { setPendingInvoiceCount(0); return; }
    const unsub = watchPendingInvoices(
      clientId,
      (docs) => {
        const pending = docs.filter((row) => String(row?.status || '').toUpperCase() === 'PENDING');
        setPendingInvoiceCount(pending.length);
      },
      () => setPendingInvoiceCount(0)
    );
    return () => { try { unsub && unsub(); } catch {} };
  }, [clientId, appId]);

  useEffect(() => {
    const clientId = String(crmClient?.id || '').trim();
    if (!clientId) {
      setRequests([]);
      setRequestsError('');
      setRequestsLoading(false);
      return;
    }

    setRequestsLoading(true);
    setRequestsError('');

    const unsubscribe = watchDocumentRequests(
      clientId,
      (rows) => {
        const sorted = (rows || [])
          .sort((a, b) => {
            const av = a?.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const bv = b?.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return bv - av;
          });
        setRequests(sorted);
        setRequestsLoading(false);
      },
      (err) => {
        setRequests([]);
        setRequestsError(String(err?.message || err || 'Falha ao carregar suas tarefas.'));
        setRequestsLoading(false);
      }
    );

    return () => {
      try {
        unsubscribe && unsubscribe();
      } catch (e) {
        void e;
      }
    };
  }, [crmClient?.id]);


  useEffect(() => {
    if (!clientId) return;

    (async () => {
      try {
        await logClientPortalEventOnce({
          clientId,
          type: 'CLIENT_PORTAL_SESSION_STARTED',
          actorName: clientName,
          payload: {
            text: 'Cliente acessou o portal do cliente.',
          },
          storage: 'session',
        });
      } catch (e) {
        void e;
      }
    })();
  }, [clientId, clientName]);

  const handleAttach = async (item, event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    const clientId = String(crmClient?.id || '').trim();
    const authUid = String(auth?.currentUser?.uid || '').trim();
    const itemId = String(item?.id || '').trim();
    if (!clientId || !itemId) return;

    const previousStatus = item?.status;

    setUploadErrorById((current) => ({ ...current, [itemId]: '' }));
    setUploadingById((current) => ({ ...current, [itemId]: true }));
    setLastUploadMessage('');

    try {
      // ── FASE 1: UPLOAD FULL-STACK (Backend Service Account → Drive) ───────
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) throw new Error('Sessão expirada. Faça login novamente.');

      const storedFileName = sanitizeFileName(file.name || 'arquivo');
      const safeName = `${Date.now()}_${storedFileName}`;
      const baseUrl = getBackendBaseUrl();
      if (!baseUrl) throw new Error('Backend não configurado.');

      const parentId = String(crmClient?.driveFolderId || crmClient?.drive?.rootFolderId || '').trim();
      if (!parentId) throw new Error('Pasta do cliente não configurada pelo escritório. Entre em contato para regularizar.');
      const form = new FormData();
      form.append('file', file, safeName);
      form.append('parentId', parentId);
      form.append('fileName', safeName);
      form.append('makePublic', 'false');

      const driveResp = await fetch(`${baseUrl}/drive/upload-file`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const driveData = await driveResp.json().catch(() => ({}));
      if (!driveResp.ok) throw new Error(String(driveData?.detail || 'Falha no upload para o Google Drive.'));

      const driveFileId = String(driveData?.fileId || driveData?.id || '').trim();
      const fileUrl = String(driveData?.webViewLink || driveData?.webContentLink || '').trim()
        || (driveFileId ? `gdrive://${driveFileId}` : '');

      if (!driveFileId) throw new Error('Google Drive não retornou o ID do arquivo após o upload.');

      // ── FASE 2: GRAVAR METADADOS NO FIRESTORE (SSOT) ────────────────
      const triagePayload = {
        clientId,
        authUid: authUid || null,
        clientName,
        clientEmail: String(email || '').trim() || null,
        documentType: String(item?.title || item?.documentType || 'Documento do Cliente').trim(),
        fileName: storedFileName,
        fileUrl,
        downloadUrl: fileUrl,
        driveFileId,
        storageEngine: { provider: 'gdrive', fileId: driveFileId, webViewLink: fileUrl },
        mimeType: String(file?.type || 'application/octet-stream').trim() || 'application/octet-stream',
        status: 'Aguardando Triagem',
        source: 'client_portal',
        taskRef: itemId,
      };

      await addToTriage(triagePayload);

      try {
        const appId = String(FIREBASE_CONFIG?.projectId || '').trim();
        if (appId) {
          await addClientNote(clientId, {
          category: 'USER',
          type: 'PORTAL_UPLOAD_SUBMITTED',
            actor: { name: clientName, isBot: false },
            payload: {
              fileName: storedFileName,
              fileUrl,
              driveFileId,
              taskRef: itemId,
              text: `Arquivo enviado pelo portal: ${storedFileName || 'arquivo'}`,
            },
          });
        }
      } catch (e) {
        void e;
      }

      setRequests((current) => current.map((requestItem) => (
        String(requestItem?.id || '') === itemId
          ? {
              ...requestItem,
              status: 'reviewing',
              lastFileName: storedFileName,
              lastFileUrl: fileUrl,
              driveFileId,
              storageEngine: { provider: 'gdrive', fileId: driveFileId, webViewLink: fileUrl },
            }
          : requestItem
      )));

      if (item?._ref) {
        await updateDocumentRequestStatus(item._ref, {
          status: 'reviewing',
          lastFileName: storedFileName,
          lastFileUrl: fileUrl,
          driveFileId,
          storageEngine: { provider: 'gdrive', fileId: driveFileId, webViewLink: fileUrl },
        });
      }

      setLastUploadMessage(`Recebemos "${storedFileName}". O escritório analisará o envio em breve.`);
      setTimeout(() => setLastUploadMessage(''), 5000);
    } catch (err) {
      setRequests((current) => current.map((requestItem) => (
        String(requestItem?.id || '') === itemId
          ? { ...requestItem, status: previousStatus || 'pending' }
          : requestItem
      )));
      setUploadErrorById((current) => ({ ...current, [itemId]: String(err?.message || err || 'Falha na conexão. Tente novamente.') }));
    } finally {
      setUploadingById((current) => ({ ...current, [itemId]: false }));
    }

    if (event?.target) {
      event.target.value = '';
    }
  };



  return (
    <div className="space-y-4">
      <PortalPageHeader
        icon={<Home className="w-5 h-5 text-[#C5A059]" />}
        title="Início"
        subtitle="Veja o que precisa da sua atenção hoje."
      />

      {/* ── Próximo Evento / Audiência ─────────────────────────────────────── */}
      {(() => {
        const ev = resolveNextEvent(portalWs) || resolveNextEvent(crmClient);
        if (!ev) return null;
        return (
          <div className="border border-gray-300 rounded-lg bg-white overflow-hidden">
            <div className="px-3 py-1.5 bg-[#20243B] flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-[#C5A059] shrink-0" />
              <span className="text-[10px] font-bold text-white uppercase tracking-wide">Próximo evento</span>
            </div>
            <div className="px-3 py-2.5 flex items-start gap-3">
              <div className="shrink-0 mt-0.5 h-9 w-9 rounded-lg bg-[#C5A059]/10 border border-[#C5A059]/30 flex flex-col items-center justify-center">
                <span className="text-[10px] font-bold text-[#C5A059] leading-none">
                  {ev.date.toLocaleDateString('pt-BR', { day: '2-digit' })}
                </span>
                <span className="text-[9px] text-[#7a5a16] leading-none uppercase">
                  {ev.date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '')}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-[#20243B] leading-tight">{ev.label}</div>
                <div className="mt-0.5 text-[11px] text-gray-600 capitalize">{ev.formatted}{ev.time ? ` — ${ev.time}` : ''}</div>
                {ev.location && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ev.location)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-0.5 flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 hover:underline w-fit"
                  >
                    <i className="fas fa-map-marker-alt text-[9px]" aria-hidden="true" />
                    {ev.location}
                  </a>
                )}
                <div className="mt-2">
                  {presenceConfirmed ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                      <UserCheck className="w-3 h-3" /> Presença confirmada
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleConfirmPresence(ev)}
                      disabled={presenceLoading}
                      className="inline-flex items-center gap-1 text-[10px] font-bold text-[#20243B] bg-[#C5A059]/10 border border-[#C5A059]/40 hover:bg-[#C5A059]/20 rounded-full px-2.5 py-1 transition-colors disabled:opacity-50"
                    >
                      <UserCheck className="w-3 h-3" />
                      {presenceLoading ? 'Confirmando...' : 'Confirmar presença'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Meus Processos (Matter-Centric Fase 3) ─────────────────────── */}
      {myWorkspaces.length > 0 ? (
        <div className="border border-gray-200 rounded-lg bg-white overflow-hidden shadow-sm">
          <div className="px-3 py-1.5 bg-[#20243B] flex items-center gap-2">
            <Briefcase className="w-3.5 h-3.5 text-[#C5A059] shrink-0" />
            <span className="text-[10px] font-bold text-white uppercase tracking-wide">Meus Processos</span>
            <span className="text-[9px] text-gray-400 ml-auto">{myWorkspaces.length}</span>
          </div>
          <div className="divide-y divide-gray-100">
            {myWorkspaces.map((ws) => {
              const npu = ws?.ancestry?.primary_process_id || null;
              const alias = String(ws?.alias || ws?.name || '').trim();
              const wsClientName = String(ws?.clientName || '').trim();
              const title = alias || (npu ? npu : null) || toTitleCasePt(wsClientName) || 'Caderno';
              const stage = ws?.caseStage || null;
              const stageLabel = stage ? ({ pre_processual: 'Pré-Processual', peticao_inicial: 'Petição Inicial', em_andamento: 'Em Andamento', recurso: 'Recurso', encerrado: 'Encerrado' }[stage] || stage) : null;
              const isCoParty = String(ws?.clientId || '') !== clientId;
              const parties = Array.isArray(ws?.partiesRefs) ? ws.partiesRefs : [];
              const myParty = parties.find((p) => String(p?.clientId || '') === clientId);
              const myRole = myParty?.role || (isCoParty ? 'Co-parte' : null);
              return (
                <button
                  key={ws.id}
                  type="button"
                  onClick={() => navigate('/portal/caso')}
                  className="w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors group flex items-center gap-3"
                >
                  <div className="shrink-0 w-8 h-8 rounded-lg bg-[#20243B]/5 border border-[#20243B]/10 flex items-center justify-center group-hover:border-[#C5A059]/40 transition-colors">
                    <Briefcase className="w-3.5 h-3.5 text-[#20243B] group-hover:text-[#C5A059] transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      {myRole && (
                        <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-[#C5A059]/10 text-[#C5A059] border border-[#C5A059]/20 shrink-0 uppercase tracking-wider">{myRole}</span>
                      )}
                      {stageLabel && (
                        <span className="text-[8px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">{stageLabel}</span>
                      )}
                    </div>
                    <div className="text-[12px] font-bold text-[#20243B] truncate leading-tight">{title}</div>
                    {npu && alias && <div className="text-[10px] text-gray-400 mt-0.5 font-mono truncate">{npu}</div>}
                  </div>
                  <i className="fas fa-chevron-right text-[9px] text-gray-300 group-hover:text-[#C5A059] transition-colors shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Estado 'tudo em dia' — todos contadores zerados */}
      {allClear ? (
        <div className="rounded-lg border border-emerald-300 bg-white px-4 py-3 flex items-center gap-3 shadow-sm">
          <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          </div>
          <div>
            <div className="text-sm font-bold text-[#20243B]">Tudo em dia!</div>
            <div className="text-[11px] text-gray-500">Nenhuma pendência, documento para revisar ou fatura em aberto.</div>
          </div>
        </div>
      ) : null}

      {/* Próxima Ação — destaca a urgência mais crítica com CTA */}
      {!allClear ? (() => {
        let label, cta, action, color;
        if (pendingSignatureCount > 0) {
          label = pendingSignatureCount === 1 ? 'O escritório precisa de 1 documento seu.' : `O escritório precisa de ${pendingSignatureCount} documentos seus.`;
          cta = 'Ver o que enviar →';
          action = () => navigate('/portal/documentos');
          color = 'red';
        } else if (pendingInvoiceCount > 0) {
          label = pendingInvoiceCount === 1 ? 'Você tem 1 fatura em aberto.' : `Você tem ${pendingInvoiceCount} faturas em aberto.`;
          cta = 'Ver fatura →';
          action = () => navigate('/portal/financeiro');
          color = 'amber';
        } else if (pendingCount > 0) {
          label = pendingCount === 1 ? 'O escritório precisa de 1 documento seu.' : `O escritório precisa de ${pendingCount} documentos seus.`;
          cta = 'Ver o que enviar →';
          action = () => { const el = document.getElementById('checklist-section'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
          color = 'amber';
        }
        if (!label) return null;
        const isRed = color === 'red';
        return (
          <div className={`rounded-lg overflow-hidden border-l-4 ${isRed ? 'border-l-red-500 bg-white border border-red-200' : 'border-l-[#C5A059] bg-white border border-[#C5A059]/30'} px-4 py-3 flex items-center gap-3 shadow-sm`}>
            <AlertCircle className={`w-5 h-5 shrink-0 ${isRed ? 'text-red-500' : 'text-[#C5A059]'}`} />
            <div className="flex-1 min-w-0">
              <div className={`text-[10px] font-bold uppercase tracking-wide ${isRed ? 'text-red-600' : 'text-[#C5A059]'}`}>Sua próxima ação</div>
              <div className="text-sm font-bold text-[#20243B]">{label}</div>
            </div>
            <button
              type="button"
              onClick={action}
              className={`shrink-0 text-xs font-bold rounded-lg px-4 py-2 transition-colors shadow-sm ${isRed ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-[#C5A059] hover:bg-[#b08a3e] text-[#20243B]'}`}
            >
              {cta}
            </button>
          </div>
        );
      })() : null}

      {/* Hero Zone: contadores com contraste forte */}
      {!allClear ? (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <button
          type="button"
          onClick={() => { const el = document.getElementById('checklist-section'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
          className={`rounded-lg border px-3 py-3 flex flex-col gap-1 text-left transition-all hover:shadow-md ${pendingCount > 0 ? 'bg-white border-[#C5A059]/40 shadow-sm' : 'bg-white border-gray-200'}`}
        >
          <div className="flex items-center justify-between w-full">
            <div className={`text-2xl font-extrabold leading-none ${pendingCount > 0 ? 'text-[#C5A059]' : 'text-gray-300'}`}>{pendingCount}</div>
            {pendingCount > 0 ? <AlertCircle className="w-4 h-4 text-[#C5A059]" /> : <CheckCircle2 className="w-4 h-4 text-gray-300" />}
          </div>
          <div className="text-[10px] font-bold text-[#20243B]">{pendingCount === 1 ? 'Envio pendente' : 'Envios pendentes'}</div>
        </button>

        <button
          type="button"
          onClick={() => navigate('/portal/documentos')}
          className={`rounded-lg border px-3 py-3 flex flex-col gap-1 text-left transition-all hover:shadow-md ${pendingSignatureCount > 0 ? 'bg-white border-red-300 shadow-sm' : 'bg-white border-gray-200'}`}
        >
          <div className="flex items-center justify-between w-full">
            <div className={`text-2xl font-extrabold leading-none ${pendingSignatureCount > 0 ? 'text-red-500' : 'text-gray-300'}`}>{pendingSignatureCount}</div>
            <FileCheck className={`w-4 h-4 ${pendingSignatureCount > 0 ? 'text-red-500' : 'text-gray-300'}`} />
          </div>
          <div className="text-[10px] font-bold text-[#20243B]">{pendingSignatureCount === 1 ? 'Doc. para revisar' : 'Docs. para revisar'}</div>
        </button>

        <button
          type="button"
          onClick={() => navigate('/portal/financeiro')}
          className={`rounded-lg border px-3 py-3 flex flex-col gap-1 text-left transition-all hover:shadow-md ${pendingInvoiceCount > 0 ? 'bg-white border-[#C5A059]/40 shadow-sm' : 'bg-white border-gray-200'}`}
        >
          <div className="flex items-center justify-between w-full">
            <div className={`text-2xl font-extrabold leading-none ${pendingInvoiceCount > 0 ? 'text-[#C5A059]' : 'text-gray-300'}`}>{pendingInvoiceCount}</div>
            <CreditCard className={`w-4 h-4 ${pendingInvoiceCount > 0 ? 'text-[#C5A059]' : 'text-gray-300'}`} />
          </div>
          <div className="text-[10px] font-bold text-[#20243B]">{pendingInvoiceCount === 1 ? 'Fatura em aberto' : 'Faturas em aberto'}</div>
        </button>

        <button
          type="button"
          onClick={() => { const el = document.getElementById('checklist-section'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-3 flex flex-col gap-1 text-left transition-all hover:shadow-md"
        >
          <div className="flex items-center justify-between w-full">
            <div className="text-2xl font-extrabold text-[#20243B] leading-none">{requests.length}</div>
            <Clock3 className="w-4 h-4 text-gray-400" />
          </div>
          <div className="text-[10px] font-bold text-[#20243B]">Todas as solicitações</div>
        </button>
      </div>
      ) : null}

      {lastUploadMessage ? (
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 shadow-sm text-xs font-bold text-blue-800">
          {lastUploadMessage}
        </div>
      ) : null}

      {requestsError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 shadow-sm text-xs font-bold text-red-800 whitespace-pre-wrap">
          {requestsError}
        </div>
      ) : null}

      <ClientTasksCard />

      {/* ── Prompt de indicação — caso encerrado ─────────────────────────── */}
      {(portalWs?.caseStage === 'encerrado' || crmClient?.caseStage === 'encerrado') && (
        <div className="border border-gray-300 rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-1.5 bg-[#20243B] flex items-center gap-2">
            <span className="text-sm leading-none">🎉</span>
            <span className="text-[10px] font-bold text-white uppercase tracking-wide">Caso concluído</span>
          </div>
          <div className="px-3 py-2.5">
            <div className="text-xs font-bold text-[#20243B]">Seu caso foi concluído!</div>
            <div className="text-[10px] text-gray-500 mt-0.5">Se conhece alguém que precise de ajuda jurídica, indique o escritório.</div>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`Olá! Precisa de um advogado de confiança? Recomendo o escritório ${LAW_FIRM_INFO.name} — ${LAW_FIRM_INFO.oab}. Entre em contato: ${LAW_FIRM_INFO.contact.whatsappLink}`)}`}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1FA855] text-white text-[10px] font-bold rounded-lg px-2.5 py-1.5 transition-colors"
            >
              <i className="fab fa-whatsapp text-xs" aria-hidden="true" />
              Indicar para um amigo
            </a>
          </div>
        </div>
      )}

      <div id="checklist-section" className="border border-gray-200 bg-white rounded-lg overflow-hidden shadow-sm">
        <div className="px-4 py-2.5 border-b border-gray-200 bg-[#20243B] flex items-center justify-between">
          <div className="text-xs font-bold text-white">O que o escritório precisa de você</div>
          <div className="text-[10px] text-[#C5A059] font-medium">Envie somente o que estiver marcado como pendente.</div>
        </div>
        <div className="p-3 space-y-2">
          {requestsLoading ? (
            <div className="rounded-lg border border-gray-300 bg-gray-50 p-5 text-sm text-gray-500 portal-loading-deferred">Carregando suas solicitações...</div>
          ) : requests.length === 0 && dspePendingSlots.length === 0 ? (
            <div className="rounded-lg border border-gray-300 bg-gray-50 p-4 text-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto mb-1.5" />
              <div className="text-xs font-bold text-[#20243B]">Nada pendente por aqui</div>
              <div className="mt-0.5 text-[10px] text-gray-600">Quando o escritório precisar de algum documento seu, a solicitação aparecerá aqui.</div>
            </div>
          ) : (
            <>
              {dspePendingSlots.map((item) => (
                <DspeSlotCard
                  key={item.id}
                  item={item}
                  onNavigate={() => navigate('/portal/caso')}
                />
              ))}
              {requests.map((item) => (
                <ChecklistCard
                  key={item.id}
                  item={item}
                  uploading={Boolean(uploadingById[String(item.id)])}
                  uploadError={uploadErrorById[String(item.id)]}
                  onAttach={handleAttach}
                  clientId={clientId}
                  appId={appId}
                />
              ))}
            </>
          )}
        </div>
      </div>

      {/* Widget de Feedback (NPS-lite) — só aparece se cliente já tem alguma atividade e não dispensou */}
      <FeedbackWidget />
    </div>
  );
}

function FeedbackWidget() {
  const STORAGE_KEY = 'portal_feedback_v2';
  const { crmClient } = usePortalContext();
  const clientId = String(crmClient?.id || '').trim();
  const appId = String(FIREBASE_CONFIG?.projectId || '').trim();

  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [picked, setPicked] = useState(null);
  const [comment, setComment] = useState('');
  const [sent, setSent] = useState(false);

  if (dismissed || sent) return null;

  const OPTIONS = [
    { id: 'great', emoji: '😊', label: 'Ótimo' },
    { id: 'ok',    emoji: '😐', label: 'Regular' },
    { id: 'hard',  emoji: '😟', label: 'Difícil' },
  ];

  const handlePick = (opt) => setPicked(opt);

  const handleSend = async () => {
    if (!picked) return;
    setSent(true);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
    if (clientId && appId) {
      try {
        await addClientNote(clientId, {
          type: 'PORTAL_FEEDBACK_GERAL',
          category: 'FEEDBACK',
            payload: { rating: picked.id, label: picked.label, comment: comment.trim() || null },
          }
        );
      } catch { void 0; }
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
  };

  return (
    <div className="border border-gray-300 rounded-lg bg-white overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-[#20243B]">Como está sendo sua experiência no portal?</div>
          <div className="text-[10px] text-gray-500">1 toque — leva menos de 5 segundos.</div>
        </div>
        <button type="button" onClick={handleDismiss} className="shrink-0 text-[10px] text-gray-400 hover:text-gray-600 uppercase tracking-wide">
          Agora não
        </button>
      </div>

      <div className="px-3 py-2.5 flex gap-2">
        {OPTIONS.map(opt => (
          <button
            key={opt.id}
            type="button"
            onClick={() => handlePick(opt)}
            className={`flex flex-col items-center gap-1 flex-1 py-2 rounded-lg border transition-all ${
              picked?.id === opt.id
                ? 'border-[#C5A059] bg-amber-50'
                : 'border-gray-200 bg-white hover:border-[#C5A059]/50 hover:bg-amber-50/40'
            }`}
          >
            <span className="text-2xl leading-none">{opt.emoji}</span>
            <span className="text-[10px] font-bold text-gray-600">{opt.label}</span>
          </button>
        ))}
      </div>

      {picked && (
        <div className="px-3 pb-3 space-y-2">
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Quer nos contar mais? (opcional)"
            rows={2}
            className="w-full text-[11px] rounded-lg border border-gray-200 px-2.5 py-1.5 resize-none outline-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/20"
          />
          <button
            type="button"
            onClick={handleSend}
            className="w-full py-1.5 rounded-lg bg-[#20243B] text-white text-[10px] font-bold uppercase tracking-wide hover:opacity-90 transition-opacity"
          >
            Enviar avaliação
          </button>
        </div>
      )}
    </div>
  );
}
